'use client';

import {
  IconBrandGithub,
  IconBrandLinkedin,
  IconBrandX,
} from '@tabler/icons-react';
import { useParams, useSearchParams } from 'next/navigation';
import Link from 'next/link';

import { Button } from '../elements/button';
import ShootingStars from '@/components/decorations/shooting-star';
import StarBackground from '@/components/decorations/star-background';
import { AnimatedTooltip } from '@/components/ui/animated-tooltip';

export function FormNextToSection({
  heading,
  sub_heading,
  form,
  section,
  social_media_icon_links,
}: {
  heading: string;
  sub_heading: string;
  form: any;
  section: any;
  social_media_icon_links: any;
}) {
  const params = useParams();
  const searchParams = useSearchParams();
  const locale = (params?.locale as string) || 'en';
  const selectedService = searchParams?.get('service');
  const selectedTraining = searchParams?.get('training');

  const defaultMessageValue = selectedService
    ? (locale === 'fr'
        ? `Bonjour, je souhaite obtenir un devis pour le service suivant : ${selectedService}.`
        : `Hello, I would like to request a quote for the following service: ${selectedService}.`)
    : selectedTraining
    ? (locale === 'fr'
        ? `Bonjour, je souhaite obtenir des informations pour la formation suivante : ${selectedTraining}.`
        : `Hello, I would like to request information about the following training: ${selectedTraining}.`)
    : undefined;

  const socials = [
    {
      title: 'twitter',
      href: 'https://twitter.com/strapijs',
      icon: (
        <IconBrandX className="h-5 w-5 text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white" />
      ),
    },
    {
      title: 'github',
      href: 'https://github.com/strapi',
      icon: (
        <IconBrandGithub className="h-5 w-5 text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white" />
      ),
    },
    {
      title: 'linkedin',
      href: 'https://linkedin.com/strapi',
      icon: (
        <IconBrandLinkedin className="h-5 w-5 text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white" />
      ),
    },
  ];

  return (
    <div className="w-full min-h-screen grid grid-cols-1 md:grid-cols-2 relative overflow-hidden">
      <div className="flex relative z-20 items-center w-full justify-center px-4 py-4 lg:py-40 sm:px-6 lg:flex-none lg:px-20  xl:px-24">
        <div className="mx-auto w-full max-w-md">
          <div>
            <h1 className="mt-8 text-2xl font-bold leading-9 tracking-tight text-neutral-900 dark:text-white">
              {heading}
            </h1>
            <p className="mt-4 text-neutral-600 dark:text-neutral-400 text-sm max-w-sm">{sub_heading}</p>
          </div>

          <div className="py-10">
            <div>
              <form className="space-y-4">
                {form &&
                  form?.inputs?.map((input: any, index: number) => (
                    <div key={`form-input-${index}`}>
                      {input.type !== 'submit' && (
                        <label
                          htmlFor={input.type === 'textarea' ? 'message' : 'name'}
                          className="block text-sm font-medium leading-6 text-neutral-700 dark:text-neutral-300"
                        >
                          {input.name}
                        </label>
                      )}

                      <div className="mt-2">
                        {input.type === 'textarea' ? (
                          <textarea
                            rows={5}
                            id="message"
                            placeholder={input.placeholder}
                            defaultValue={defaultMessageValue}
                            className="block w-full bg-white dark:bg-neutral-900 px-4 rounded-md border border-neutral-300 dark:border-neutral-800 py-2 shadow-sm text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 focus:ring-2 focus:ring-primary focus:outline-none sm:text-sm sm:leading-6"
                          />
                        ) : input.type === 'submit' ? (
                          <div>
                            <Button className="w-full mt-6">
                              {input.name}
                            </Button>
                          </div>
                        ) : (
                          <input
                            id="name"
                            type={input.type}
                            placeholder={input.placeholder}
                            className="block w-full bg-white dark:bg-neutral-900 px-4 rounded-md border border-neutral-300 dark:border-neutral-800 py-2 shadow-sm text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 focus:ring-2 focus:ring-primary focus:outline-none sm:text-sm sm:leading-6"
                          />
                        )}
                      </div>
                    </div>
                  ))}
              </form>
            </div>
          </div>
          <div className="flex items-center justify-center space-x-4 py-4">
            {socials.map((social) => (
              <Link href={social.href} target="_blank" key={social.title}>
                {social.icon}
              </Link>
            ))}
          </div>
        </div>
      </div>
      <div className="relative w-full z-20 hidden md:flex border-l border-charcoal overflow-hidden bg-neutral-900 items-center justify-center">
        <StarBackground />
        <ShootingStars />
        <div className="max-w-sm mx-auto">
          <div className="flex flex-row items-center justify-center mb-10 w-full">
            <AnimatedTooltip items={section.users} />
          </div>
          <p
            className={
              'font-semibold text-xl text-center  text-muted text-balance'
            }
          >
            {section.heading}
          </p>
          <p
            className={
              'font-normal text-base text-center text-neutral-500  mt-8 text-balance'
            }
          >
            {section.sub_heading}
          </p>
        </div>
      </div>
    </div>
  );
}
